import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Hooks
import useIsomorphicLayoutEffect from 'hooks/use-isomorphic-layout-effect';

// Utils
import bemify from 'utils/bemify';

// Components
import Button from 'components/button';
import Input from 'components/input';
import RadioGroup from 'components/radio-group';
import TagButton from 'components/tag-button';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);


const RestaurantMenuSidebarFilter = ({
  className = null,
  items = null,
  maximumValue = null,
  minimumValue = null,
  onChange = () => {},
  onResetButtonClick = () => {},
  rangeValues = null,
  selectedValue,
  showResetButton = false,
  title = '',
  translations = null,
  type = 'checkbox'
}) => {
  const priceInputRefs = useRef([]);
  const rangeInputRefs = useRef([]);
  const rangeRef = useRef(null);

  const updateProgressBar = useCallback((minValue, maxValue) => {
    rangeRef.current.style.left = `${
      ((minValue - rangeInputRefs.current[0].min) / (rangeInputRefs.current[0].max - rangeInputRefs.current[0].min)) * 100
    }%`;
    rangeRef.current.style.right = `${
      100 - (((maxValue - rangeInputRefs.current[1].min) / (rangeInputRefs.current[1].max - rangeInputRefs.current[1].min)) * 100)
    }%`;
  }, []);

  const handleCheckboxChange = (value) => {
    onChange(value);
  };

  const handleRadioChange = (value) => {
    onChange(value);
  };

  const minimumRangeGap = Math.ceil((maximumValue - minimumValue) / 20);

  const handleRangeChange = (context, field) => {
    if (
      Array.isArray(rangeInputRefs.current) && rangeInputRefs.current.length &&
      Array.isArray(priceInputRefs.current) && priceInputRefs.current.length
    ) {
      let minValue = null;
      let maxValue = null;

      if (context === 'range') {
        minValue = parseInt(rangeInputRefs.current?.[0]?.value, 10);
        maxValue = parseInt(rangeInputRefs.current?.[1]?.value, 10);

        if (field === 'min' && maxValue - minValue < minimumRangeGap) {
          minValue = maxValue - minimumRangeGap;
        } else if (field === 'max' && maxValue - minValue < minimumRangeGap) {
          maxValue = minValue + minimumRangeGap;
        }
      } else if (context === 'number') {
        minValue = parseInt(priceInputRefs.current?.[0]?.value, 10);
        maxValue = parseInt(priceInputRefs.current?.[1]?.value, 10);
      }

      if (
        !isNaN(maxValue) && !isNaN(minValue) &&
        maxValue - minValue >= minimumRangeGap &&
        minValue >= minimumValue &&
        maxValue <= maximumValue
      ) {
        onChange({
          maximum: maxValue,
          minimum: minValue
        });
      }
    }
  };

  const handleRangeNumberBlur = (field) => {
    let minValue = parseInt(priceInputRefs.current?.[0]?.value, 10);
    let maxValue = parseInt(priceInputRefs.current?.[1]?.value, 10);

    if (isNaN(minValue) || minValue < minimumValue) minValue = minimumValue;
    if (isNaN(maxValue) || maxValue > maximumValue) maxValue = maximumValue;

    if (field === 'min' && maxValue - minValue < minimumRangeGap) {
      minValue = maxValue - minimumRangeGap;
    } else if (field === 'max' && maxValue - minValue < minimumRangeGap) {
      maxValue = minValue + minimumRangeGap;
    }

    onChange({
      maximum: maxValue,
      minimum: minValue
    });
  };

  useIsomorphicLayoutEffect(() => {
    if (rangeRef.current) {
      updateProgressBar(minimumValue, maximumValue);
    }
  }, [maximumValue, minimumValue, updateProgressBar]);

  useEffect(() => {
    if (
      Array.isArray(rangeInputRefs.current) && rangeInputRefs.current.length &&
      Array.isArray(priceInputRefs.current) && priceInputRefs.current.length
    ) {
      rangeInputRefs.current[0].value = rangeValues.minimum;
      rangeInputRefs.current[1].value = rangeValues.maximum;
      priceInputRefs.current[0].value = rangeValues.minimum;
      priceInputRefs.current[1].value = rangeValues.maximum;

      updateProgressBar(rangeValues.minimum, rangeValues.maximum);
    }
  }, [rangeValues, updateProgressBar]);

  if ((!Array.isArray(items) || !items.length) && type !== 'range') return null;

  return (
    <div className={`${styles.restaurantMenuSidebarFilter}${className ? ` ${className}` : ''}`}>
      <div className={styles.restaurantMenuSidebarFilter__header}>
        <h6 className={styles.restaurantMenuSidebarFilter__title}>
          {title}
        </h6>
        {onResetButtonClick && showResetButton && (
          <Button
            noBackground
            paddingSize="none"
            size="s"
            text={translations.resetFilterButtonLabel}
            underlineOnHover
            onClick={onResetButtonClick}
          />
        )}
      </div>
      {type === 'radio' && (
        <RadioGroup
          acceptEmptyValue
          items={items}
          value={selectedValue}
          onChange={handleRadioChange}
        />
      )}
      {type === 'checkbox' && (
        <div className={styles.restaurantMenuSidebarFilter__checkboxGroup}>
          {items.map((entry, index) => (
            <TagButton
              key={index}
              checked={entry.checked}
              icon={entry.icon}
              id={entry.icon}
              isCheckbox
              label={entry.label}
              onCheckboxChange={handleCheckboxChange}
            />
          ))}
        </div>
      )}
      {type === 'range' && (
        <>
          <div className={styles.restaurantMenuSidebarFilter__rangeGroup}>
            <Input
              ref={(element) => {
                priceInputRefs.current[0] = element;
              }}
              className={styles.restaurantMenuSidebarFilter__rangeNumberInput}
              defaultValue={minimumValue}
              label={translations.rangeMinimumPriceLabel}
              max={maximumValue}
              min={minimumValue}
              placeholder={translations.rangeMinimumPricePlaceholder}
              step="1"
              type="number"
              onBlur={() => handleRangeNumberBlur('min')}
              onChange={() => handleRangeChange('number', 'min')}
            />
            <div className={styles.restaurantMenuSidebarFilter__fieldSeparator}>
              -
            </div>
            <Input
              ref={(element) => {
                priceInputRefs.current[1] = element;
              }}
              className={styles.restaurantMenuSidebarFilter__rangeNumberInput}
              defaultValue={maximumValue}
              label={translations.rangeMaximumPriceLabel}
              max={maximumValue}
              min={minimumValue}
              placeholder={translations.rangeMaximumPricePlaceholder}
              step="1"
              type="number"
              onBlur={() => handleRangeNumberBlur('max')}
              onChange={() => handleRangeChange('number', 'max')}
            />
          </div>
          <div className={styles.restaurantMenuSidebarFilter__slider}>
            <div
              ref={rangeRef}
              className={styles.restaurantMenuSidebarFilter__sliderProgress}
            />
          </div>
          <div className={styles.restaurantMenuSidebarFilter__rangeInput}>
            <input
              ref={(element) => {
                rangeInputRefs.current[0] = element;
              }}
              className={styles.restaurantMenuSidebarFilter__rangeMinimum}
              defaultValue={minimumValue}
              max={maximumValue}
              min={minimumValue}
              step="1"
              type="range"
              onChange={() => handleRangeChange('range', 'min')}
            />
            <input
              ref={(element) => {
                rangeInputRefs.current[1] = element;
              }}
              className={styles.restaurantMenuSidebarFilter__rangeMaximum}
              defaultValue={maximumValue}
              max={maximumValue}
              min={minimumValue}
              step="1"
              type="range"
              onChange={() => handleRangeChange('range', 'max')}
            />
          </div>
        </>
      )}
    </div>
  );
};

RestaurantMenuSidebarFilter.displayName = 'RestaurantMenuSidebarFilter';

RestaurantMenuSidebarFilter.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    checked: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  maximumValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minimumValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onResetButtonClick: PropTypes.func,
  rangeValues: PropTypes.shape({
    maximum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minimum: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  selectedValue: PropTypes.string,
  showResetButton: PropTypes.bool,
  title: PropTypes.string.isRequired,
  translations: PropTypes.shape({
    rangeMaximumPriceLabel: PropTypes.string,
    rangeMaximumPricePlaceholder: PropTypes.string,
    rangeMinimumPriceLabel: PropTypes.string,
    rangeMinimumPricePlaceholder: PropTypes.string,
    resetFilterButtonLabel: PropTypes.string
  }),
  type: PropTypes.oneOf(['checkbox', 'radio', 'range']).isRequired
};

export default RestaurantMenuSidebarFilter;
